import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TimelineIcon from '@mui/icons-material/Timeline';
import ArticleIcon from '@mui/icons-material/Article';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import BugReportIcon from '@mui/icons-material/BugReport';
import LinkIcon from '@mui/icons-material/Link';
import { apiClient } from '../../framework/api/api.client';

// =============================================================================
// Types
// =============================================================================

interface TraceSpan {
  spanId: string;
  parentSpanId?: string;
  operationName: string;
  serviceName: string;
  startTime: string;
  duration: number;
  status: 'ok' | 'error' | 'unset';
  tags: Record<string, string | number | boolean>;
  logs?: Array<{ timestamp: string; message: string }>;
}

interface Trace {
  traceId: string;
  rootSpan: string;
  serviceName: string;
  operationName: string;
  startTime: string;
  duration: number;
  spanCount: number;
  status: 'ok' | 'error' | 'unset';
  spans?: TraceSpan[];
}

interface TraceSearchResult {
  traces: Trace[];
  total: number;
  limit: number;
}

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error' | 'fatal';
  message: string;
  service: string;
  traceId?: string;
  spanId?: string;
  labels: Record<string, string>;
  fields?: Record<string, any>;
}

interface LogSearchResult {
  logs: LogEntry[];
  total: number;
  limit: number;
  source: 'loki' | 'local' | 'mock';
}

interface ServiceMetrics {
  serviceName: string;
  requestRate: number;
  errorRate: number;
  p50Latency: number;
  p95Latency: number;
  p99Latency: number;
}

interface ObservabilityCapabilities {
  traces: {
    enabled: boolean;
    provider: 'tempo' | 'jaeger' | 'local' | 'mock' | null;
    url?: string;
  };
  logs: {
    enabled: boolean;
    provider: 'loki' | 'local' | 'mock';
    url?: string;
  };
  metrics: {
    enabled: boolean;
    provider: 'prometheus' | 'mimir' | 'mock' | null;
  };
}

// =============================================================================
// Styled Components
// =============================================================================

const ObservabilityCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(2),
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(1.5),
}));

const TraceRow = styled(TableRow)<{ status: string }>(({ theme, status }) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  ...(status === 'error' && {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    '&:hover': {
      backgroundColor: 'rgba(239, 68, 68, 0.15)',
    },
  }),
}));

const LogRow = styled(TableRow)<{ level: string }>(({ level }) => ({
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
  },
  ...(level === 'error' && {
    backgroundColor: 'rgba(239, 68, 68, 0.08)',
  }),
  ...(level === 'warn' && {
    backgroundColor: 'rgba(245, 158, 11, 0.08)',
  }),
}));

const SpanBar = styled(Box)<{ depth: number; isError: boolean }>(({ depth, isError }) => ({
  height: 24,
  borderRadius: 4,
  backgroundColor: isError ? 'rgba(239, 68, 68, 0.8)' : 'rgba(96, 165, 250, 0.8)',
  marginLeft: depth * 20,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: 8,
  paddingRight: 8,
  fontSize: '0.75rem',
  color: '#fff',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

// =============================================================================
// Helper Functions
// =============================================================================

const formatDuration = (microseconds: number): string => {
  if (microseconds < 1000) return `${microseconds}µs`;
  if (microseconds < 1000000) return `${(microseconds / 1000).toFixed(1)}ms`;
  return `${(microseconds / 1000000).toFixed(2)}s`;
};

const formatTimestamp = (iso: string): string => {
  const date = new Date(iso);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit', 
    second: '2-digit',
    hour12: false 
  });
};

const getLevelIcon = (level: string) => {
  switch (level) {
    case 'error':
    case 'fatal':
      return <ErrorIcon sx={{ fontSize: 16, color: '#ef4444' }} />;
    case 'warn':
      return <WarningIcon sx={{ fontSize: 16, color: '#f59e0b' }} />;
    case 'info':
      return <InfoIcon sx={{ fontSize: 16, color: '#60a5fa' }} />;
    case 'debug':
      return <BugReportIcon sx={{ fontSize: 16, color: '#a78bfa' }} />;
    default:
      return <InfoIcon sx={{ fontSize: 16, color: '#9ca3af' }} />;
  }
};

const getLevelColor = (level: string): string => {
  switch (level) {
    case 'error':
    case 'fatal':
      return '#ef4444';
    case 'warn':
      return '#f59e0b';
    case 'info':
      return '#60a5fa';
    case 'debug':
      return '#a78bfa';
    default:
      return '#9ca3af';
  }
};

// =============================================================================
// Main Component
// =============================================================================

export const ObservabilityView: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState<ObservabilityCapabilities | null>(null);
  
  // Traces state
  const [traces, setTraces] = useState<Trace[]>([]);
  const [tracesLoading, setTracesLoading] = useState(false);
  const [selectedTrace, setSelectedTrace] = useState<Trace | null>(null);
  const [traceFilters, setTraceFilters] = useState({ service: '', status: '', minDuration: '' });
  
  // Logs state
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logSource, setLogSource] = useState<string>('');
  const [logFilters, setLogFilters] = useState({ query: '', level: '', service: '', traceId: '' });
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  
  // Service metrics state
  const [serviceMetrics, setServiceMetrics] = useState<ServiceMetrics[]>([]);
  
  // Polling
  const [isPolling, setIsPolling] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // =============================================================================
  // Data Fetching
  // =============================================================================

  const fetchCapabilities = useCallback(async () => {
    try {
      const caps = await apiClient.get<ObservabilityCapabilities>('/observability/capabilities');
      setCapabilities(caps);
    } catch (error) {
      console.error('Failed to fetch observability capabilities:', error);
    }
  }, []);

  const fetchTraces = useCallback(async () => {
    setTracesLoading(true);
    try {
      const params = new URLSearchParams();
      if (traceFilters.service) params.append('service', traceFilters.service);
      if (traceFilters.status) params.append('status', traceFilters.status);
      if (traceFilters.minDuration) params.append('minDuration', traceFilters.minDuration);
      params.append('limit', '50');
      
      const result = await apiClient.get<TraceSearchResult>(`/observability/traces?${params.toString()}`);
      setTraces(result.traces || []);
    } catch (error) {
      console.error('Failed to fetch traces:', error);
    } finally {
      setTracesLoading(false);
    }
  }, [traceFilters]);

  const fetchTraceDetail = useCallback(async (traceId: string) => {
    try {
      const trace = await apiClient.get<Trace>(`/observability/traces/${traceId}?traceId=${traceId}`);
      setSelectedTrace(trace);
    } catch (error) {
      console.error('Failed to fetch trace detail:', error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams();
      if (logFilters.query) params.append('query', logFilters.query);
      if (logFilters.level) params.append('level', logFilters.level);
      if (logFilters.service) params.append('service', logFilters.service);
      if (logFilters.traceId) params.append('traceId', logFilters.traceId);
      params.append('limit', '200');
      
      const result = await apiClient.get<LogSearchResult>(`/observability/logs?${params.toString()}`);
      setLogs(result.logs || []);
      setLogSource(result.source || 'unknown');
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLogsLoading(false);
    }
  }, [logFilters]);

  // Helper to set trace ID filter and switch to logs tab
  const filterLogsByTraceId = useCallback((traceId: string) => {
    setLogFilters(prev => ({ ...prev, traceId }));
    setActiveTab(1); // Switch to logs tab
  }, []);

  const fetchServiceMetrics = useCallback(async () => {
    try {
      const result = await apiClient.get<{ services: ServiceMetrics[]; timestamp: string }>('/observability/services');
      setServiceMetrics(result.services || []);
    } catch (error) {
      console.error('Failed to fetch service metrics:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      await fetchCapabilities();
      setLoading(false);
    };
    initialize();
  }, [fetchCapabilities]);

  // Load data when tab changes
  useEffect(() => {
    if (activeTab === 0) {
      fetchServiceMetrics();
      fetchTraces();
    } else if (activeTab === 1) {
      fetchLogs();
    }
  }, [activeTab, fetchTraces, fetchLogs, fetchServiceMetrics]);

  // Polling
  useEffect(() => {
    if (isPolling) {
      pollingRef.current = setInterval(() => {
        if (activeTab === 0) {
          fetchTraces();
          fetchServiceMetrics();
        } else {
          fetchLogs();
        }
      }, 5000);
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isPolling, activeTab, fetchTraces, fetchLogs, fetchServiceMetrics]);

  // =============================================================================
  // Render Helpers
  // =============================================================================

  const toggleLogExpanded = (logId: string) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const isMockMode = capabilities?.traces.provider === 'mock' || capabilities?.logs.provider === 'mock';
  const isLocalTraceMode = capabilities?.traces.provider === 'local';
  const traceStoreInfo = (capabilities as any)?.traceStore;

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            Observability
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            {capabilities?.traces.enabled && (
              <Chip
                size="small"
                label={`Traces: ${capabilities.traces.provider || 'disabled'}${traceStoreInfo?.traceCount ? ` (${traceStoreInfo.traceCount})` : ''}`}
                sx={{
                  backgroundColor: capabilities.traces.provider === 'mock' 
                    ? 'rgba(168, 85, 247, 0.2)'
                    : capabilities.traces.provider === 'local'
                    ? 'rgba(96, 165, 250, 0.2)'
                    : 'rgba(52, 211, 153, 0.2)',
                  color: capabilities.traces.provider === 'mock' 
                    ? '#a855f7' 
                    : capabilities.traces.provider === 'local'
                    ? '#60a5fa'
                    : '#34d399',
                  fontSize: '0.7rem',
                }}
              />
            )}
            {capabilities?.logs.enabled && (
              <Chip
                size="small"
                label={`Logs: ${capabilities.logs.provider || 'disabled'}`}
                sx={{
                  backgroundColor: capabilities.logs.provider === 'mock' 
                    ? 'rgba(168, 85, 247, 0.2)' 
                    : 'rgba(52, 211, 153, 0.2)',
                  color: capabilities.logs.provider === 'mock' ? '#a855f7' : '#34d399',
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Box>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          <Chip
            size="small"
            label={isPolling ? 'Live' : 'Paused'}
            onClick={() => setIsPolling(!isPolling)}
            sx={{
              cursor: 'pointer',
              backgroundColor: isPolling ? 'rgba(34, 197, 94, 0.2)' : 'rgba(156, 163, 175, 0.2)',
              color: isPolling ? '#22c55e' : '#9ca3af',
            }}
          />
          <Tooltip title="Refresh">
            <IconButton 
              onClick={() => activeTab === 0 ? fetchTraces() : fetchLogs()} 
              size="small"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Mock Mode Notice */}
      {isMockMode && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2, 
            backgroundColor: 'rgba(168, 85, 247, 0.1)',
            border: '1px solid rgba(168, 85, 247, 0.3)',
          }}
        >
          <Typography variant="body2">
            <strong>🎭 Mock Mode Active</strong> — Displaying simulated observability data.
            Set <code style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: 4 }}>TEMPO_URL</code> and{' '}
            <code style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: 4 }}>LOKI_URL</code> to connect to real backends.
          </Typography>
        </Alert>
      )}

      {/* Local Trace Mode Notice */}
      {isLocalTraceMode && !isMockMode && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 2, 
            backgroundColor: 'rgba(96, 165, 250, 0.1)',
            border: '1px solid rgba(96, 165, 250, 0.3)',
          }}
        >
          <Typography variant="body2">
            <strong>📊 Local Trace Store Active</strong> — Showing real HTTP request traces captured in-memory
            {traceStoreInfo?.traceCount !== undefined && (
              <> (storing <strong>{traceStoreInfo.traceCount}</strong> traces)</>
            )}
            . Child spans (database, cache, serialization) are <em>simulated</em> to preview production-like data.
            {' '}For full instrumentation, configure <code style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: 4 }}>TEMPO_URL</code> or{' '}
            <code style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: 4 }}>JAEGER_URL</code>.
          </Typography>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(_, v) => setActiveTab(v)}
        sx={{ 
          mb: 3,
          '& .MuiTab-root': { 
            textTransform: 'none', 
            fontWeight: 600,
            minHeight: 48,
          },
        }}
      >
        <Tab icon={<TimelineIcon />} iconPosition="start" label="Traces" />
        <Tab icon={<ArticleIcon />} iconPosition="start" label="Logs" />
      </Tabs>

      {/* Traces Tab */}
      {activeTab === 0 && (
        <Box>
          {/* Service Metrics Summary */}
          {serviceMetrics.length > 0 && (
            <Grid container spacing={2} mb={3}>
              {serviceMetrics.map((svc) => (
                <Grid item xs={12} sm={6} md={3} key={svc.serviceName}>
                  <MetricCard elevation={0}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      {svc.serviceName}
                    </Typography>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="textSecondary">Rate</Typography>
                      <Typography variant="caption" fontFamily="monospace">{svc.requestRate.toFixed(1)}/s</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="caption" color="textSecondary">Errors</Typography>
                      <Typography 
                        variant="caption" 
                        fontFamily="monospace"
                        sx={{ color: svc.errorRate > 1 ? '#ef4444' : '#22c55e' }}
                      >
                        {svc.errorRate.toFixed(2)}%
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="caption" color="textSecondary">P95 Latency</Typography>
                      <Typography 
                        variant="caption" 
                        fontFamily="monospace"
                        sx={{ color: svc.p95Latency > 500 ? '#f59e0b' : 'inherit' }}
                      >
                        {svc.p95Latency}ms
                      </Typography>
                    </Box>
                  </MetricCard>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Trace Filters */}
          <ObservabilityCard sx={{ mb: 3 }}>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  size="small"
                  label="Service"
                  value={traceFilters.service}
                  onChange={(e) => setTraceFilters({ ...traceFilters, service: e.target.value })}
                  sx={{ minWidth: 150 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={traceFilters.status}
                    label="Status"
                    onChange={(e) => setTraceFilters({ ...traceFilters, status: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="ok">OK</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Min Duration (ms)"
                  type="number"
                  value={traceFilters.minDuration}
                  onChange={(e) => setTraceFilters({ ...traceFilters, minDuration: e.target.value })}
                  sx={{ minWidth: 140 }}
                />
                <Button 
                  variant="outlined" 
                  startIcon={<SearchIcon />}
                  onClick={fetchTraces}
                  sx={{ textTransform: 'none' }}
                >
                  Search
                </Button>
              </Box>
            </CardContent>
          </ObservabilityCard>

          {/* Traces Table */}
          <ObservabilityCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Recent Traces
              </Typography>
              
              {tracesLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>TRACE ID</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>SERVICE</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>OPERATION</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>DURATION</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>SPANS</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>TIME</TableCell>
                        <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>STATUS</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {traces.map((trace) => (
                        <React.Fragment key={trace.traceId}>
                          <TraceRow 
                            status={trace.status}
                            onClick={() => {
                              if (selectedTrace?.traceId === trace.traceId) {
                                setSelectedTrace(null);
                              } else {
                                fetchTraceDetail(trace.traceId);
                              }
                            }}
                          >
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {trace.traceId.substring(0, 12)}...
                            </TableCell>
                            <TableCell>{trace.serviceName}</TableCell>
                            <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {trace.operationName}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>
                              {formatDuration(trace.duration)}
                            </TableCell>
                            <TableCell>{trace.spanCount}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>
                              {formatTimestamp(trace.startTime)}
                            </TableCell>
                            <TableCell>
                              {trace.status === 'error' ? (
                                <ErrorIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                              ) : (
                                <CheckCircleIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                              )}
                            </TableCell>
                          </TraceRow>
                          
                          {/* Trace Detail (Waterfall) */}
                          {selectedTrace?.traceId === trace.traceId && selectedTrace.spans && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ p: 0 }}>
                                <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.2)' }}>
                                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      Trace Waterfall
                                    </Typography>
                                    <Button
                                      size="small"
                                      startIcon={<ArticleIcon sx={{ fontSize: 14 }} />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        filterLogsByTraceId(selectedTrace.traceId);
                                      }}
                                      sx={{
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        color: '#60a5fa',
                                        '&:hover': { backgroundColor: 'rgba(96, 165, 250, 0.1)' },
                                      }}
                                    >
                                      View Logs for this Trace
                                    </Button>
                                  </Box>
                                  <Box>
                                    {/* Local mode simulated spans notice */}
                                    {isLocalTraceMode && selectedTrace.spans.some(s => s.tags?.simulated) && (
                                      <Alert 
                                        severity="info" 
                                        sx={{ 
                                          mb: 2, 
                                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                          border: '1px solid rgba(59, 130, 246, 0.2)',
                                          '& .MuiAlert-icon': { color: '#3b82f6' },
                                        }}
                                      >
                                        <Typography variant="caption">
                                          <strong>Local Mode Preview:</strong> Child spans (database, cache, serialization) are simulated to preview production-like traces. 
                                          In production with Tempo/Jaeger, these would be real instrumented operations.
                                        </Typography>
                                      </Alert>
                                    )}
                                    {selectedTrace.spans.map((span, idx) => {
                                      const depth = span.parentSpanId ? 1 : 0;
                                      const widthPercent = Math.max(5, (span.duration / selectedTrace.duration) * 100);
                                      const isSimulated = span.tags?.simulated === true;
                                      
                                      return (
                                        <Box key={span.spanId} mb={0.5}>
                                          <Box display="flex" alignItems="center" gap={1}>
                                            <Typography 
                                              variant="caption" 
                                              sx={{ 
                                                minWidth: 80, 
                                                fontFamily: 'monospace',
                                                marginLeft: depth * 20,
                                                color: isSimulated ? 'rgba(168, 85, 247, 0.8)' : 'inherit',
                                              }}
                                            >
                                              {formatDuration(span.duration)}
                                            </Typography>
                                            <Box sx={{ flex: 1, position: 'relative', height: 24 }}>
                                              <SpanBar 
                                                depth={0} 
                                                isError={span.status === 'error'}
                                                sx={{ 
                                                  width: `${widthPercent}%`,
                                                  ...(isSimulated && {
                                                    background: 'linear-gradient(90deg, rgba(168, 85, 247, 0.7) 0%, rgba(139, 92, 246, 0.5) 100%)',
                                                    borderStyle: 'dashed',
                                                  }),
                                                }}
                                              >
                                                {span.operationName}
                                                {isSimulated && (
                                                  <Typography 
                                                    component="span" 
                                                    sx={{ 
                                                      fontSize: '0.6rem', 
                                                      ml: 1, 
                                                      opacity: 0.7,
                                                      fontStyle: 'italic',
                                                    }}
                                                  >
                                                    (simulated)
                                                  </Typography>
                                                )}
                                              </SpanBar>
                                            </Box>
                                            {/* Show span tags */}
                                            {span.tags && Object.keys(span.tags).filter(k => k !== 'simulated').length > 0 && (
                                              <Tooltip 
                                                title={
                                                  <Box>
                                                    {Object.entries(span.tags)
                                                      .filter(([k]) => k !== 'simulated')
                                                      .map(([key, value]) => (
                                                        <Typography key={key} variant="caption" display="block">
                                                          {key}: {String(value)}
                                                        </Typography>
                                                      ))}
                                                  </Box>
                                                }
                                              >
                                                <Chip
                                                  size="small"
                                                  label={`${Object.keys(span.tags).filter(k => k !== 'simulated').length} tags`}
                                                  sx={{
                                                    height: 16,
                                                    fontSize: '0.6rem',
                                                    backgroundColor: 'rgba(100, 100, 100, 0.2)',
                                                  }}
                                                />
                                              </Tooltip>
                                            )}
                                          </Box>
                                          {span.logs && span.logs.length > 0 && (
                                            <Box ml={depth * 20 + 10} mt={0.5}>
                                              {span.logs.map((log, logIdx) => (
                                                <Typography 
                                                  key={logIdx} 
                                                  variant="caption" 
                                                  sx={{ color: '#ef4444', display: 'block' }}
                                                >
                                                  ⚠️ {log.message}
                                                </Typography>
                                              ))}
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {traces.length === 0 && !tracesLoading && (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary">No traces found</Typography>
                </Box>
              )}
            </CardContent>
          </ObservabilityCard>
        </Box>
      )}

      {/* Logs Tab */}
      {activeTab === 1 && (
        <Box>
          {/* Log Source Indicator */}
          <Box mb={2}>
            <Chip
              size="small"
              label={`Source: ${logSource}`}
              sx={{
                backgroundColor: logSource === 'loki' 
                  ? 'rgba(251, 146, 60, 0.2)' 
                  : logSource === 'local' 
                    ? 'rgba(96, 165, 250, 0.2)'
                    : 'rgba(168, 85, 247, 0.2)',
                color: logSource === 'loki' 
                  ? '#fb923c' 
                  : logSource === 'local' 
                    ? '#60a5fa'
                    : '#a855f7',
                fontSize: '0.7rem',
              }}
            />
          </Box>

          {/* Log Filters */}
          <ObservabilityCard sx={{ mb: 3 }}>
            <CardContent sx={{ py: 2 }}>
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField
                  size="small"
                  label="Search"
                  value={logFilters.query}
                  onChange={(e) => setLogFilters({ ...logFilters, query: e.target.value })}
                  sx={{ minWidth: 200 }}
                  placeholder="Search in messages..."
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Level</InputLabel>
                  <Select
                    value={logFilters.level}
                    label="Level"
                    onChange={(e) => setLogFilters({ ...logFilters, level: e.target.value })}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                    <MenuItem value="warn">Warning</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="debug">Debug</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Service"
                  value={logFilters.service}
                  onChange={(e) => setLogFilters({ ...logFilters, service: e.target.value })}
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  size="small"
                  label="Trace ID"
                  value={logFilters.traceId}
                  onChange={(e) => setLogFilters({ ...logFilters, traceId: e.target.value })}
                  sx={{ minWidth: 180 }}
                  placeholder="Filter by trace..."
                  InputProps={{
                    startAdornment: logFilters.traceId ? (
                      <LinkIcon sx={{ fontSize: 16, color: '#60a5fa', mr: 0.5 }} />
                    ) : undefined,
                  }}
                />
                <Button 
                  variant="outlined" 
                  startIcon={<SearchIcon />}
                  onClick={fetchLogs}
                  sx={{ textTransform: 'none' }}
                >
                  Search
                </Button>
                {(logFilters.query || logFilters.level || logFilters.service || logFilters.traceId) && (
                  <Button 
                    variant="text" 
                    size="small"
                    onClick={() => setLogFilters({ query: '', level: '', service: '', traceId: '' })}
                    sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.6)' }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
              {logFilters.traceId && (
                <Box mt={1}>
                  <Chip
                    size="small"
                    icon={<LinkIcon sx={{ fontSize: 14 }} />}
                    label={`Filtering by trace: ${logFilters.traceId.substring(0, 16)}...`}
                    onDelete={() => setLogFilters({ ...logFilters, traceId: '' })}
                    sx={{
                      backgroundColor: 'rgba(96, 165, 250, 0.2)',
                      color: '#60a5fa',
                      '& .MuiChip-deleteIcon': {
                        color: '#60a5fa',
                        '&:hover': { color: '#93c5fd' },
                      },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </ObservabilityCard>

          {/* Logs Table */}
          <ObservabilityCard>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Log Entries ({logs.length})
              </Typography>
              
              {logsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', width: 40 }}></TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', width: 90 }}>TIME</TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', width: 70 }}>LEVEL</TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', width: 120 }}>SERVICE</TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}>MESSAGE</TableCell>
                        <TableCell sx={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', width: 100 }}>TRACE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {logs.map((log) => (
                        <React.Fragment key={log.id}>
                          <LogRow 
                            level={log.level}
                            onClick={() => toggleLogExpanded(log.id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              <IconButton size="small" sx={{ p: 0 }}>
                                {expandedLogs.has(log.id) ? (
                                  <ExpandLessIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <ExpandMoreIcon sx={{ fontSize: 16 }} />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {formatTimestamp(log.timestamp)}
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                {getLevelIcon(log.level)}
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: getLevelColor(log.level),
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                  }}
                                >
                                  {log.level}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{log.service}</TableCell>
                            <TableCell sx={{ 
                              fontSize: '0.8rem', 
                              maxWidth: 400, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {log.message}
                            </TableCell>
                            <TableCell>
                              {log.traceId && (
                                <Tooltip title={`Click to view trace: ${log.traceId}`}>
                                  <Chip
                                    size="small"
                                    icon={<LinkIcon sx={{ fontSize: 12 }} />}
                                    label={log.traceId.substring(0, 8)}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // Switch to traces tab and search for this trace
                                      setActiveTab(0);
                                      setTraceFilters({ ...traceFilters, service: '' });
                                      // Trigger search for this trace ID
                                      fetchTraceDetail(log.traceId!);
                                    }}
                                    sx={{
                                      height: 20,
                                      fontSize: '0.65rem',
                                      fontFamily: 'monospace',
                                      backgroundColor: 'rgba(96, 165, 250, 0.2)',
                                      color: '#60a5fa',
                                      cursor: 'pointer',
                                      '&:hover': {
                                        backgroundColor: 'rgba(96, 165, 250, 0.4)',
                                      },
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                          </LogRow>
                          
                          {/* Expanded Log Details */}
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0, borderBottom: 'none' }}>
                              <Collapse in={expandedLogs.has(log.id)}>
                                <Box sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.2)' }}>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                      <Typography variant="caption" color="textSecondary">Full Message</Typography>
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          fontFamily: 'monospace', 
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-all',
                                          backgroundColor: 'rgba(0,0,0,0.3)',
                                          p: 1,
                                          borderRadius: 1,
                                          mt: 0.5,
                                        }}
                                      >
                                        {log.message}
                                      </Typography>
                                    </Grid>
                                    {log.labels && Object.keys(log.labels).length > 0 && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="textSecondary">Labels</Typography>
                                        <Box mt={0.5}>
                                          {Object.entries(log.labels).map(([key, value]) => (
                                            <Chip
                                              key={key}
                                              size="small"
                                              label={`${key}: ${value}`}
                                              sx={{
                                                mr: 0.5,
                                                mb: 0.5,
                                                height: 20,
                                                fontSize: '0.65rem',
                                                backgroundColor: 'rgba(156, 163, 175, 0.2)',
                                              }}
                                            />
                                          ))}
                                        </Box>
                                      </Grid>
                                    )}
                                    {log.fields?.stack && (
                                      <Grid item xs={12}>
                                        <Typography variant="caption" color="textSecondary">Stack Trace</Typography>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontFamily: 'monospace', 
                                            whiteSpace: 'pre-wrap',
                                            fontSize: '0.7rem',
                                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                            p: 1,
                                            borderRadius: 1,
                                            mt: 0.5,
                                            color: '#ef4444',
                                          }}
                                        >
                                          {log.fields.stack}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {logs.length === 0 && !logsLoading && (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary">No logs found</Typography>
                </Box>
              )}
            </CardContent>
          </ObservabilityCard>
        </Box>
      )}
    </Box>
  );
};

export default ObservabilityView;

